import { useState } from "react";
import { Copy, Check } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { TIER_LABEL, type PlaybookSection, type PlaybookBlock } from "@/data/partnerPlaybook";

function CopyButton({ text }: { text: string }) {
  const [ok, setOk] = useState(false);
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className="h-7 px-2 text-xs"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setOk(true);
          toast.success("Copied");
          setTimeout(() => setOk(false), 1500);
        } catch {
          toast.error("Copy failed");
        }
      }}
    >
      {ok ? <Check className="h-3.5 w-3.5 mr-1" /> : <Copy className="h-3.5 w-3.5 mr-1" />}
      {ok ? "Copied" : "Copy"}
    </Button>
  );
}

function Block({ block }: { block: PlaybookBlock }) {
  switch (block.type) {
    case "p":
      return <p className="leading-relaxed">{block.text}</p>;
    case "list":
      return (
        <ul className="list-disc pl-5 space-y-1.5 marker:text-muted-foreground">
          {block.items.map((it, i) => <li key={i}>{it}</li>)}
        </ul>
      );
    case "script":
      return (
        <Card className="bg-muted/30 border-border">
          <div className="flex items-center justify-between px-3 py-2 border-b border-border">
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{block.label}</div>
            <CopyButton text={block.body} />
          </div>
          <pre className="p-3 text-xs sm:text-sm whitespace-pre-wrap font-mono text-foreground/90 overflow-x-auto">{block.body}</pre>
        </Card>
      );
    case "table":
      return (
        <div className="overflow-x-auto">
          {block.caption && <div className="text-xs text-muted-foreground mb-2">{block.caption}</div>}
          <table className="w-full text-sm border border-border rounded-md overflow-hidden">
            <thead className="bg-muted/40">
              <tr>
                {block.headers.map((h, i) => (
                  <th key={i} className="text-left px-3 py-2 font-semibold border-b border-border">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, ri) => (
                <tr key={ri} className="border-b border-border last:border-b-0">
                  {row.map((c, ci) => <td key={ci} className="px-3 py-2 align-top">{c}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case "callout":
      return (
        <div className={`rounded-md border p-3 text-sm ${
          block.tone === "warn"
            ? "border-amber-500/40 bg-amber-500/5"
            : "border-primary/30 bg-primary/5"
        }`}>
          {block.title && <div className="font-semibold mb-1">{block.title}</div>}
          <div className="text-foreground/90">{block.text}</div>
        </div>
      );
  }
}

export default function PlaybookSectionView({ section }: { section: PlaybookSection }) {
  return (
    <section id={section.id} className="scroll-mt-24">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <h2 className="font-display text-2xl font-semibold">{section.title}</h2>
        {section.tiers.map((t) => (
          <Badge key={t} variant="outline" className="text-[10px] uppercase tracking-wider">{TIER_LABEL[t]}</Badge>
        ))}
      </div>
      <div className="text-[11px] text-muted-foreground mb-4">
        Updated {new Date(section.updatedAt).toLocaleDateString()} · v{section.version}
      </div>
      <div className="space-y-4 text-foreground/90">
        {section.blocks.map((b, i) => <Block key={i} block={b} />)}
      </div>
    </section>
  );
}